import { useCallback, useMemo } from 'react';
import { gramsPerDay, macroTotalsDrift, mealTargets } from '../lib/nutrition';
import { MACRO_KEYS, mealSource } from '../components/steps/mealTargets.shared';
import type { BuilderDerived, Dispatch } from './useDietPlanBuilder';
import type { MacroKey, Plan, Template } from '../types';

export function useMealTargetsStep(
  plan: Plan,
  derived: NonNullable<BuilderDerived>,
  tpl: Template | null,
  dispatch: Dispatch,
) {
  const gp = gramsPerDay(plan.kcal, plan.pct, 'p');
  const gc = gramsPerDay(plan.kcal, plan.pct, 'c');
  const gf = gramsPerDay(plan.kcal, plan.pct, 'f');
  const tplShares = plan.templateMealShares ?? tpl?.shares;
  const tplRatios = plan.templateMealRatios ?? tpl?.ratios;

  const rows = useMemo(
    () =>
      plan.meals.map((m, i) => {
        const T = mealTargets(plan, m);
        const editedMacro = (k: MacroKey) => m.ov[k] != null;
        return { m, i, T, editedMacro, source: mealSource(plan, m) };
      }),
    [plan],
  );

  const active = rows.filter(r => !r.m.skip);
  const shareSum = derived.shareSum;
  const sumShare = Math.round(active.reduce((a, r) => a + (+r.m.share || 0), 0) * 10) / 10;
  const barDenom = Math.max(sumShare, 100);

  const totals = active.reduce(
    (a, r) => ({
      share: a.share + (+r.m.share || 0),
      k: a.k + r.T.k,
      p: a.p + r.T.p,
      c: a.c + r.T.c,
      f: a.f + r.T.f,
    }),
    { share: 0, k: 0, p: 0, c: 0, f: 0 },
  );

  const macroDrifts = useMemo(
    () => macroTotalsDrift({ p: totals.p, c: totals.c, f: totals.f }, { p: gp, c: gc, f: gf }),
    [totals.p, totals.c, totals.f, gp, gc, gf],
  );
  const macroDrift = useMemo(() => {
    const hit = { p: false, c: false, f: false };
    macroDrifts.forEach(d => { hit[d.key] = true; });
    return hit;
  }, [macroDrifts]);

  const set = useCallback((fn: (p: Plan) => void) => dispatch({ type: 'plan', fn }), [dispatch]);
  const setSkip = useCallback((i: number, skip: boolean) => set(p => { p.meals[i].skip = skip; }), [set]);
  const setTime = useCallback((i: number, time: string) => set(p => { p.meals[i].time = time; }), [set]);
  const setEndTime = useCallback((i: number, endTime: string) => set(p => { p.meals[i].endTime = endTime; }), [set]);
  const setShare = useCallback((i: number, share: number) => set(p => {
    p.meals[i].share = Math.max(0, Math.round(share * 10) / 10);
  }), [set]);
  const setKcal = useCallback((i: number, v: number) => set(p => {
    p.meals[i].share = p.kcal ? Math.round((Math.max(0, v) / p.kcal) * 10000) / 100 : 0;
  }), [set]);
  const setMacro = useCallback((i: number, k: MacroKey, v: number) => set(p => {
    p.meals[i].ov = { ...p.meals[i].ov, [k]: Math.max(0, v) };
  }), [set]);
  const useTemplate = useCallback(() => {
    if (tplShares) {
      dispatch({
        type: 'split',
        mode: 'template',
        tplShares,
        tplRatios,
        tplTimes: plan.templateMealTimes ?? undefined,
      });
    }
  }, [dispatch, tplShares, tplRatios, plan.templateMealTimes]);
  const useStandard = useCallback(() => dispatch({ type: 'split', mode: 'standard' }), [dispatch]);
  const scaleShares = useCallback(() => dispatch({ type: 'scaleShares' }), [dispatch]);

  return {
    gp, gc, gf, rows, active, shareSum, barDenom, totals, macroDrifts, macroDrift,
    canTemplate: plan.templateId != null && !!tplShares?.length,
    split: plan.split,
    setSkip, setTime, setEndTime, setShare, setKcal, setMacro, useTemplate, useStandard, scaleShares,
    MACRO_KEYS,
  };
}
